import { useEffect, useState } from "react"

import { Alert, App, Button, Flex, Layout, Modal, Space, Spin, Tag, Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"
import ReactPlayer from "react-player"
import { shallowEqual, useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router"
import { useLocation, useNavigate } from "react-router-dom"

import ButtonComponent from "../../../Components/Button"
import ConnectPaymentProcessorForm from "../../../Components/Forms/ConnectPaymentProcessorForm"
import Tour from "../../../Components/Tour"
import { stepsTourConnectPaymentProcessor } from "../../../Components/Tour/stepsTourConnectPaymentProcessot"
import Icon from "../../../Icon"
import { modalConfig } from "../../../theme"
import { highAccessRoles } from "../../../Utils/permission"
import utils, {
  createAccountStepPath,
  handleViewTutorial,
  manageEnrollmentStepPath,
  onboardingStepPath,
  paymentProcessorDescription,
  paymentProcessorMap
} from "../../../Utils/utils"
import { BA_STEPS } from "../../GettingStartedPage/utils/businessSteps"
import { OB_STEPS } from "../../GettingStartedPage/utils/onboardingSteps"
import IntegrationCard from "../../IntegrationsPage/components/Card"
import ConnectNextcommerce from "../../IntegrationsPage/Nextcommerce/ConnectNextcommerce"
import useNextcommerceDisconnect from "../../IntegrationsPage/Nextcommerce/hooks/useNextcommerveDisconnect"
import AuthActions from "../../LoginPage/reducer"
import UserActions from "../../User/reducer"
import { updateCompletedStep } from "../../User/services"
import MobileCtaButtons from "./components/MobileCtaButtons"

import "./styles.less"

const { Title, Text } = Typography

const {
  changeOnboardingStepRequest,
  changeNextCreateAccountStep,
  getPaymentProcessorsRequest,
  connectPaymentProcessorRequest,
  disconnectPaymentProcessorRequest,
  userSaveResultReset,
  userSetProp
} = UserActions
const { updateProfile } = AuthActions

const MOST_POPULAR = new Set(["stripe", "shopify", "manual_refunds"])

const splitProcessors = (processors = []) => {
  const mostPopular = []
  const moreIntegrations = []

  processors.forEach((p) => {
    if (MOST_POPULAR.has(p.service_name)) {
      mostPopular.push(p)
    } else {
      moreIntegrations.push(p)
    }
  })

  return { mostPopular, moreIntegrations }
}

const OnboardingPaymentConnectPage = (props) => {
  const { step, maxStep } = props

  const dispatch = useDispatch()
  const { enrollmentId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { notification, modal } = App.useApp()

  const handleNavigate = () => {
    if (isNewBusiness) {
      navigate(createAccountStepPath[7])
      return
    }
    navigate(onboardingStepPath[2])
  }

  const [openTour, setOpenTour] = useState(false)

  // show modal
  const [visible, setVisible] = useState(false)
  // video or connectForm
  const [modalType, setModalType] = useState("")

  const [connectPayment, setConnectPayment] = useState(null)
  const [customConnectServiceName, setCustomConnectServiceName] = useState(null)
  const [continueAfterConnect, setContinueAfterConnect] = useState(false)

  const { fetching, userInfo, isAdmin } = useSelector((s) => s.auth)
  const isMobile = useSelector((s) => s.nav.isMobile)
  const {
    fetching: userFetching,
    businessAccountFetching,
    paymentProcessors,
    businessAccount,
    nextCreateAccountStep,
    saveResult,
    isOneProcessorConnected,
    businessAccountsWithRole: businessAccounts
  } = useSelector((s) => s.user, shallowEqual)

  const hasHighAccessRole = businessAccounts?.some(({ role }) =>
    highAccessRoles?.includes(role?.id)
  )

  const isNewBusiness = location.pathname?.includes("/create-account")

  const currentBusinessAccountId = userInfo?.["current_business_account_id"]
  let businessId =
    businessAccount?.id === currentBusinessAccountId
      ? businessAccount?.id
      : currentBusinessAccountId
  let selectedPayment = paymentProcessors
    ?.filter((pp) => pp?.["is_connected"])
    ?.map((pp) => pp?.["service_name"])
  let selectedNotManual = selectedPayment.length && !selectedPayment?.includes("manual_refunds")
  const normalizedProcessors = paymentProcessors?.map((el) => ({
    ...el,
    is_connected: el.is_connected,
    title: el.title || paymentProcessorMap?.[el.service_name] || el.service_name,
    isDisabled: el.isDisabled || (el.service_name === "manual_refunds" && selectedNotManual)
  }))
  const { mostPopular, moreIntegrations } = splitProcessors(normalizedProcessors)

  const notificationConfig = {
    closeIcon: <Text>{t("Dismiss")}</Text>,
    style: { minWidth: "max-content" }
  }

  const handleCloseModal = () => {
    setVisible(false)
    setModalType("")
    if (saveResult?.["connect_error"]) dispatch(userSaveResultReset())
  }

  useEffect(() => {
    if (userInfo && businessId) {
      dispatch(
        getPaymentProcessorsRequest({
          merchant_id: businessId,
          return_url: process.env.REACT_APP_BASE_URI + location.pathname
        })
      )
    }
    document.title = "Payment processor connect - Chargeback"
    /*eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, [fetching, businessId])

  useEffect(() => {
    if (!connectPayment) {
      if (userInfo?.["new_onboarding_finished"]) {
        if (enrollmentId) {
        } else {
          dispatch(changeNextCreateAccountStep(2))
        }
      } else {
        if (userInfo?.next_step - 2 > step) {
          dispatch(changeOnboardingStepRequest({ next_step: step + 2 }))

          updateCompletedStep({
            step: step - 1,
            business_account_id: businessId
          })
        }
      }
    }
  }, [connectPayment])

  useEffect(() => {
    if ((saveResult === "connected" || saveResult === "disconnected") && connectPayment) {
      let namePayment =
        paymentProcessorMap?.[connectPayment.service_name] ||
        utils.toUpperLatter(connectPayment.service_name)
      let message = null
      let description = null
      if (saveResult === "connected") {
        if (connectPayment.service_name === "manual_refunds") {
          message = t("Manual Refunds Selected")
          description = t("Manual Refunds was selected successfully")
        } else {
          message = t("Payment processor connected")
          description = t("_namePayment_ was connected successfully.", {
            namePayment
          })
        }
      } else if (saveResult === "disconnected") {
        if (connectPayment.service_name === "manual_refunds") {
          message = t("Manual Refunds Deselected")
          description = t("Manual Refunds was deselected successfully")
        } else {
          message = t("Payment processor disconnected")
          description = t("_namePayment_ was disconnected successfully.", {
            namePayment
          })
        }

        if (
          !isOneProcessorConnected &&
          connectPayment.service_name !== "manual_refunds" &&
          enrollmentId
        ) {
          dispatch(
            getPaymentProcessorsRequest({
              merchant_id: businessId,
              return_url: process.env.REACT_APP_BASE_URI + location.pathname
            })
          )
        }
      }
      notification.success({
        message,
        description,
        ...notificationConfig
      })
      if (
        saveResult === "connected" &&
        continueAfterConnect &&
        connectPayment.service_name === "manual_refunds"
      ) {
        setContinueAfterConnect(false)
        handleSubmit()
      }
      handleCloseModal()
      dispatch(userSaveResultReset())
    }
  }, [saveResult, isOneProcessorConnected, continueAfterConnect])

  const handleContinue = () => {
    const manual = mostPopular.find((i) => i.service_name === "manual_refunds")
    setContinueAfterConnect(true)
    handleClick(manual)
  }

  async function handleSubmit() {
    if (selectedPayment?.length) {
      if (nextCreateAccountStep) {
        await updateCompletedStep({ step, business_account_id: businessId })
        dispatch(changeNextCreateAccountStep(3))
        if (userInfo?.is_created_by_invitation) {
          dispatch(updateProfile({ next_step: 5 }))
          dispatch(UserActions.userSaveRequest({ next_step: 5 }))
        }
        navigate(createAccountStepPath[step + 1])
      } else if (enrollmentId) {
        dispatch(userSetProp("nextManageEnrollmentStep", 2))

        navigate(manageEnrollmentStepPath(enrollmentId, isAdmin)[2], {
          replace: true
        })
      } else {
        if (userInfo?.next_step - 2 === step)
          dispatch(changeOnboardingStepRequest({ next_step: 5 }))

        await updateCompletedStep({ step, business_account_id: businessId })
        dispatch(updateProfile({ next_step: 5 }))
        navigate(onboardingStepPath[step + 2 + 1])
      }
    } else {
      modal.warning({
        ...modalConfig("exclamation", 442),
        className: `ob-clear-bg cdrn ${isMobile && "mobile"}`,
        okButtonProps: { size: "large", style: { width: "auto" } },
        cancelButtonProps: { size: "large", style: { width: "auto" } },
        centered: !isMobile,
        okCancel: true,
        onOk() {
          handleContinue()
        },

        title: t("No integration connected"),
        content: (
          <Flex vertical gap={26}>
            <div>
              <Text type={"secondary"}>
                While not required for alerts, we <Text bold>recommend</Text> connecting your
                gateway or billing system to automate alert to transaction matching and
                auto-refunds.
              </Text>
            </div>
            <div>
              <Text type={"secondary"}>
                If you continue without selecting a processor, <Text bold>“Other”</Text> will be
                enabled by default. You can always connect integration later.
              </Text>
            </div>
          </Flex>
        ),
        okText: t("Continue"),
        cancelText: t("Cancel")
      })
    }
  }

  const { disconnectNext } = useNextcommerceDisconnect()

  const disconnectPayment = (payment) => {
    modal.confirm({
      ...modalConfig("exclamation"),
      className: "ob-clear-bg cdrn",
      centered: !isMobile,

      title: t("Disconnect payment processor"),
      content: t("Are you sure you want to disconnect your payment processor?"),

      cancelText: t("Cancel"),
      okText: t("Disconnect"),
      cancelButtonProps: { width: 70, height: 40 },
      okButtonProps: {
        width: 75,
        height: 40
      },
      onOk() {
        if (payment.service_name === "next") {
          disconnectNext(businessId)
          return
        }
        dispatch(
          disconnectPaymentProcessorRequest({
            payment_processor_id: payment.id,
            service_name: payment.service_name
          })
        )
      },
      onCancel() {
        console.log("Cancel")
      }
    })
  }

  const handleConnectPaymentWithForm = (data) => {
    if (!connectPayment) return
    dispatch(
      connectPaymentProcessorRequest({
        service_name: connectPayment.service_name,
        merchant_id: connectPayment.merchant_id,
        ...data
      })
    )
  }

  const handleClick = (payment) => {
    if (payment.is_connected) {
      setConnectPayment(payment)
      disconnectPayment(payment)
    } else if (payment?.service_name === "next") {
      setCustomConnectServiceName(payment.service_name)
    } else {
      if (!payment.link && payment?.["list_of_required_fields_for_tokens"].length) {
        setConnectPayment(payment)
        if (payment?.service_name === "adyen") {
          setModalType("connectForm")
          setVisible(true)
        } else {
          setOpenTour(true)
        }
      } else {
        if (payment.service_name === "manual_refunds") {
          setConnectPayment(payment)
          dispatch(
            connectPaymentProcessorRequest({
              service_name: payment.service_name,
              payment_processor_id: payment.id
            })
          )
        } else {
          if (payment.link) utils.goLink(payment.link)
        }
      }
    }
  }

  const handleCloseTour = (closedBy) => {
    if (closedBy === "mask") {
      setOpenTour(false)
      return
    }
    setOpenTour(false)
    setModalType("connectForm")
    setVisible(true)
  }

  const handleOpenTour = () => {
    setOpenTour(true)
    setModalType(null)
    setVisible(false)
  }

  const handleBack = () => {
    if (isNewBusiness) {
      return navigate(createAccountStepPath[BA_STEPS.COMPANY])
    }
    navigate(onboardingStepPath[OB_STEPS.COMPANY])
  }

  const TutorialButton = ({ serviceName, isMobile }) => {
    const isAdyen = serviceName === "adyen"

    return (
      <>
        {!isMobile ? (
          <ButtonComponent
            type="text"
            size="large"
            className="flex center"
            style={isMobile ? { width: "100%" } : { marginRight: "auto" }}
            icon={isAdyen ? null : <Icon type="arrow_left" />}
            onClick={isAdyen ? handleViewTutorial : handleOpenTour}
          >
            {t("View tutorial")}
          </ButtonComponent>
        ) : (
          <ButtonComponent
            htmlType="button"
            size="large"
            className="flex center"
            style={isMobile ? { width: "100%" } : { marginRight: "auto" }}
            onClick={isAdyen ? handleViewTutorial : handleOpenTour}
          >
            {t("View tutorial")}
          </ButtonComponent>
        )}
      </>
    )
  }

  const modalBlocks = {
    connectForm: {
      classNames: {
        content: cls("clear-bg full-width-header", {
          "bg-center": modalType === "success",
          "payment-connect": modalType === "connectForm",
          recurly: connectPayment?.["service_name"] === "recurly"
        }),
        header: cls({
          "payment-connect": modalType === "connectForm",
          [`${connectPayment?.["service_name"]}`]: modalType === "connectForm"
        })
      },
      title: (
        <Space direction="vertical">
          <Text className="payment-connect-title">
            {t("_namePayment_ integration", {
              namePayment: paymentProcessorMap[connectPayment?.["service_name"]]
            })}
          </Text>
          <Text className="payment-connect-sub_title">
            {t(
              paymentProcessorDescription[connectPayment?.["service_name"]] ??
                "Connect your _namePayment_ account to fully automate your _namePayment_ disputes.",
              {
                namePayment: paymentProcessorMap[connectPayment?.["service_name"]]
              }
            )}
          </Text>
        </Space>
      ),
      component: (
        <ConnectPaymentProcessorForm
          fields={connectPayment?.["list_of_required_fields_for_tokens"]?.filter(
            (el) => el !== "merchant_id"
          )}
          onClose={handleCloseModal}
          service_name={connectPayment?.service_name}
          onSubmit={handleConnectPaymentWithForm}
          disabled={businessAccountFetching}
          error={saveResult?.["connect_error"]}
          extraBtn={
            <TutorialButton serviceName={connectPayment?.service_name} isMobile={isMobile} />
          }
          isMobile={isMobile}
        />
      )
    },
    video: {
      classNames: { wrapper: "video-modal", content: "clear-bg" },
      title: null,
      component: (
        <ReactPlayer
          controls={true}
          width={"100%"}
          height={"100%"}
          playing={true}
          url={"https://www.youtube.com/watch?v=OftbjjbL0tc"}
        />
      )
    }
  }

  return (
    <Layout>
      <Spin
        wrapperClassName="payment-connect-spinner"
        size="large"
        spinning={fetching || businessAccountFetching || userFetching}
      >
        <div className="on-boarding-content">
          <div className="content-main">
            <Flex
              vertical
              align={"center"}
              style={{
                width: "100%",
                maxWidth: 1040
              }}
            >
              <div className="on-boarding-content__header">
                {!isMobile && (
                  <Button
                    className="back-btn"
                    type="text"
                    icon={<Icon type="arrow_left" />}
                    onClick={handleBack}
                  >
                    Back
                  </Button>
                )}
                <h2 className="card-title">{t("Add an integration")}</h2>
                <h4 className="card-subtitle-signup">
                  {t("You can always connect or switch integration later.")}
                </h4>
              </div>
              <Alert
                className="primary notice-alert"
                icon={<Icon type="warning_full" />}
                showIcon
                message={<Text strong>{t("Important notice")}</Text>}
                description={
                  <Text>
                    {t(
                      enrollmentId
                        ? "Please be aware that connecting or disconnecting an integration will affect the entire business account, not just the selected enrollment."
                        : "Connect a payment gateway or billing system for automatic chargeback prevention. If it’s not visible, select Other instead."
                    )}
                  </Text>
                }
                style={{
                  margin: !isMobile ? "32px 0 24px" : "16px 0 24px",
                  width: "100%"
                }}
              />
              <Flex justify="start" style={{ width: "100%", marginBottom: 14 }}>
                <Tag className="integration_type integration_type--blue">Most popular</Tag>
              </Flex>

              <Flex
                gap={16}
                wrap="wrap"
                justify="center"
                align="stretch"
                className="responsive-flex"
                style={{
                  marginBottom: 32,
                  maxWidth: "994px",
                  alignSelf: "flex-start"
                }}
              >
                {mostPopular.map((payment) => {
                  return (
                    <IntegrationCard
                      key={payment.id}
                      data={payment}
                      style={{ width: 320 }}
                      onClick={handleClick}
                      disabled={
                        !isAdmin &&
                        !hasHighAccessRole &&
                        location.pathname.includes("/configure/enrollment")
                      }
                    />
                  )
                })}
              </Flex>
              <Flex justify="start" style={{ width: "100%", marginBottom: 16 }}>
                <Tag className="step-status integration_type--green">More integrations</Tag>
              </Flex>
              <Flex
                gap={16}
                wrap="wrap"
                justify="center"
                align="stretch"
                className="responsive-flex"
                style={{
                  marginBottom: isMobile ? 140 : 60,
                  maxWidth: "994px",
                  alignSelf: "flex-start"
                }}
              >
                {moreIntegrations.map((payment) => {
                  return (
                    <IntegrationCard
                      key={payment.id}
                      data={payment}
                      style={{ width: 320 }}
                      onClick={handleClick}
                      disabled={
                        !isAdmin &&
                        !hasHighAccessRole &&
                        location.pathname.includes("/configure/enrollment")
                      }
                    />
                  )
                })}
              </Flex>
              <div className="pp-sticky-cta">
                <MobileCtaButtons
                  isMobile={isMobile}
                  onSaveForLater={handleNavigate}
                  onBack={handleBack}
                />
                <ButtonComponent
                  type="primary"
                  size="large"
                  loading={userFetching}
                  onClick={handleSubmit}
                  className="pp-cta"
                >
                  {t("Continue")}
                </ButtonComponent>
              </div>
            </Flex>
          </div>
        </div>
      </Spin>

      <Tour
        open={openTour}
        className={cls("connect-payment-tour", "tour-with-skip")}
        steps={stepsTourConnectPaymentProcessor()[connectPayment?.service_name]}
        onClose={handleCloseTour}
        key={connectPayment?.service_name}
        isClosableOnMaskClick={true}
        indicatorsRender={() => (
          <ButtonComponent size="large" onClick={handleCloseTour}>
            Skip tutorial
          </ButtonComponent>
        )}
      />

      <Modal
        {...(isMobile ? { transitionName: "" } : null)}
        open={visible}
        centered={!isMobile}
        width={442}
        title={modalBlocks[modalType]?.title}
        footer={null}
        closable="true"
        closeIcon={<Icon type="close_modal" />}
        onCancel={handleCloseModal}
        destroyOnClose
        classNames={modalBlocks[modalType]?.classNames}
      >
        <Spin size="large" spinning={fetching} wrapperClassName="visible">
          {modalBlocks[modalType]?.component}
        </Spin>
      </Modal>
      {customConnectServiceName === "next" && (
        <ConnectNextcommerce
          onFinish={() => setCustomConnectServiceName(null)}
          isMobile={isMobile}
        />
      )}
    </Layout>
  )
}

export default OnboardingPaymentConnectPage
